import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Download, CheckCircle2, Loader2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { trackPurchase } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 8;

export default function EbookDownload() {
  const { sessionId } = useParams();
  const [status, setStatus] = useState({ state: "checking", info: null });
  const attempts = useRef(0);

  useEffect(() => {
    document.title = "Download · Dickies-Campus Pflegeshop";

    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const res = await axios.get(`${API}/ebooks/status/${sessionId}`);
        if (cancelled) return;
        if (res.data?.payment_status === "paid") {
          trackPurchase((res.data.amount_total || 0) / 100, sessionId, "b2c");
          setStatus({ state: "paid", info: res.data });
          return;
        }
        if (attempts.current >= MAX_ATTEMPTS) {
          setStatus({ state: "pending", info: res.data });
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        setStatus({ state: "error", info: null });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const downloadUrl = `${API}/ebooks/download/${sessionId}`;

  return (
    <div className="min-h-screen bg-cream-50 text-sand-800">
      <Header />
      <main className="pt-40 pb-24 lg:pt-48 lg:pb-32">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {status.state === "checking" && (
            <div data-testid="ebook-status-checking">
              <Loader2 className="h-10 w-10 text-terra mx-auto animate-spin" strokeWidth={1.5} />
              <h1 className="mt-6 font-display text-3xl sm:text-4xl text-sand-800 font-normal tracking-tight">
                Zahlung wird bestätigt …
              </h1>
              <p className="mt-4 text-sand-600">
                Einen Moment – wir gleichen die Zahlung mit Stripe ab.
              </p>
            </div>
          )}

          {status.state === "paid" && (
            <div data-testid="ebook-status-paid" className="relative">
              {/* Editorial section eyebrow */}
              <div className="inline-flex items-center gap-3 mb-8">
                <span className="h-px w-10 bg-terra" />
                <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-terra">
                  Editor&rsquo;s Note &middot; Nº 01
                </span>
                <span className="h-px w-10 bg-terra" />
              </div>

              {/* Editorial thank-you card — rotated paper with washi tape */}
              <div className="relative mx-auto max-w-xl">
                {/* washi-tape corners */}
                <div
                  aria-hidden="true"
                  className="absolute -top-3 left-12 h-6 w-20 bg-brand-rose/70 rotate-[-6deg] z-10 shadow-sm"
                  style={{ mixBlendMode: "multiply" }}
                />
                <div
                  aria-hidden="true"
                  className="absolute -top-3 right-12 h-6 w-20 bg-brand-rose/70 rotate-[5deg] z-10 shadow-sm"
                  style={{ mixBlendMode: "multiply" }}
                />

                <div
                  className="relative bg-cream-50 border border-cream-300/60 rotate-[-0.5deg] shadow-[0_30px_60px_-30px_rgba(82,42,37,0.35)] px-8 py-12 md:px-14 md:py-16 text-left"
                  style={{
                    backgroundImage:
                      "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='p'><feTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23p)' opacity='0.06'/></svg>\")",
                  }}
                >
                  {/* Dateline */}
                  <div className="text-[11px] uppercase tracking-[0.28em] text-sand-500 font-semibold mb-1">
                    Düsseldorf &middot; {new Date().toLocaleDateString("de-DE", { month: "long", year: "numeric" })}
                  </div>
                  <div className="h-px w-16 bg-terra mb-6" />

                  <h1 className="font-display text-2xl md:text-[1.9rem] text-sand-800 font-normal leading-tight">
                    Willkommen im Club.
                  </h1>

                  {status.info?.ebook_title && (
                    <p className="mt-3 font-display italic text-[15px] md:text-base text-sand-600">
                      Bezugsnachweis: &bdquo;{status.info.ebook_title}&ldquo;
                    </p>
                  )}

                  <p className="mt-6 text-sand-700 text-[15px] leading-[1.8]">
                    Danke, dass du auf uns vertraust. Dein Ratgeber wartet auf dich —
                    klick auf den Button und lade in Ruhe herunter. Öffne ihn beim Kaffee,
                    schreib rein, teil ihn mit den Kolleg:innen. Er gehört jetzt dir,
                    DRM-frei und ohne Zeitlimit.
                  </p>

                  {/* Editorial CTA — no rounded pill, editorial block */}
                  <div className="mt-9 flex flex-col sm:flex-row sm:items-center gap-4">
                    <a
                      href={downloadUrl}
                      download
                      data-testid="ebook-download-button"
                      className="inline-flex items-center justify-center gap-3 bg-terra text-white px-8 h-14 text-[13px] uppercase tracking-[0.22em] font-semibold hover:bg-brand-darkred transition-colors group"
                    >
                      <Download className="h-4 w-4" strokeWidth={2} />
                      PDF herunterladen
                    </a>
                    <span className="text-[12px] text-sand-500 italic">
                      DRM-frei &middot; unbegrenzte Zugriffe
                    </span>
                  </div>

                  {/* Signature block */}
                  <div className="mt-12 flex items-end justify-between gap-6 flex-wrap">
                    <div>
                      <div
                        className="text-[2rem] md:text-[2.5rem] text-terra leading-none"
                        style={{
                          fontFamily: "'Caveat', 'Homemade Apple', 'Dancing Script', cursive",
                          transform: "rotate(-2deg)",
                        }}
                      >
                        Markus
                      </div>
                      <div className="mt-2 text-[11px] uppercase tracking-[0.28em] text-sand-500 font-semibold">
                        Markus &middot; Gründer
                      </div>
                    </div>
                    {/* Rotating Studio-DH-Stempel */}
                    <div
                      aria-hidden="true"
                      className="relative border-[2px] border-terra/60 rounded-full h-20 w-20 flex flex-col items-center justify-center rotate-[-6deg] text-terra"
                      style={{ animation: "spin 60s linear infinite" }}
                    >
                      <div className="text-[9px] uppercase tracking-[0.2em] font-bold leading-none">
                        Studio
                      </div>
                      <div className="font-display text-[1.05rem] leading-none mt-1">DH</div>
                      <div className="text-[8px] tracking-[0.24em] font-semibold mt-1">2026</div>
                    </div>
                  </div>
                </div>
              </div>

              <p className="mt-10 text-[12px] text-sand-500 max-w-md mx-auto">
                Probleme beim Download? Schreib uns an{" "}
                <a href="mailto:info@dickies-helden.de" className="underline underline-offset-2 hover:text-terra">
                  info@dickies-helden.de
                </a>
                {" "}– wir helfen sofort.
              </p>
            </div>
          )}

          {status.state === "pending" && (
            <div data-testid="ebook-status-pending">
              <h1 className="font-display text-3xl sm:text-4xl text-sand-800 font-normal tracking-tight">
                Zahlung wird noch verarbeitet
              </h1>
              <p className="mt-4 text-sand-600 max-w-md mx-auto leading-relaxed">
                Manche Zahlarten brauchen ein paar Minuten. Wir senden dir den Download-Link automatisch per E-Mail, sobald die Zahlung bestätigt ist.
              </p>
              <Link to="/#pflegeshop" className="mt-7 inline-flex items-center gap-2 text-sand-800 hover:text-terra font-medium">
                <ArrowLeft className="h-4 w-4" /> Zurück zum Pflegeshop
              </Link>
            </div>
          )}

          {status.state === "error" && (
            <div data-testid="ebook-status-error">
              <h1 className="font-display text-3xl text-sand-800 font-normal tracking-tight">
                Da ist etwas schiefgelaufen.
              </h1>
              <p className="mt-4 text-sand-600">
                Schreib uns kurz an{" "}
                <a href="mailto:info@dickies-helden.de" className="underline underline-offset-2 hover:text-terra">
                  info@dickies-helden.de
                </a>{" "}
                – wir senden dir den Download manuell.
              </p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
